import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { ApprovalDateField } from "@/components/vagas/ApprovalDateField";
import { EncerrarDemaisField } from "@/components/vagas/EncerrarDemaisField";
import { isApprovalDateValid, todayLocalDate } from "@/components/vagas/approval-date";

interface AprovacaoComProjetoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: {
    encerrar_demais: boolean;
    /** Dia da aprovação ("YYYY-MM-DD"), hoje ou antes. */
    data_aprovacao: string;
  }) => void;
  isLoading?: boolean;
  vagaId: string | null;
  freelancerAprovadoId: string | null;
}

/**
 * Aprovar uma vaga que já tem projeto vinculado: não cria outro projeto, então só pede a
 * data de aprovação e se encerra os demais candidatos (os dois campos do AprovacaoModal
 * que valem aqui).
 */
export function AprovacaoComProjetoDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  vagaId,
  freelancerAprovadoId,
}: AprovacaoComProjetoDialogProps) {
  const [encerrarDemais, setEncerrarDemais] = useState<boolean>(true);
  const [approvalDate, setApprovalDate] = useState<string>("");

  useEffect(() => {
    if (open) {
      setEncerrarDemais(true);
      setApprovalDate(todayLocalDate());
    }
  }, [open]);

  const isFormValid = isApprovalDateValid(approvalDate);

  const handleConfirm = () => {
    if (!isFormValid) return;
    onConfirm({ encerrar_demais: encerrarDemais, data_aprovacao: approvalDate });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-600">
            <CheckCircle2 className="h-5 w-5" />
            Aprovar Vaga
          </DialogTitle>
          <DialogDescription>
            Esta vaga já tem um projeto vinculado, então nenhum projeto novo será criado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <ApprovalDateField value={approvalDate} onChange={setApprovalDate} disabled={isLoading} />

          <EncerrarDemaisField
            id="encerrar_demais_com_projeto"
            vagaId={vagaId}
            freelancerAprovadoId={freelancerAprovadoId}
            checked={encerrarDemais}
            onCheckedChange={setEncerrarDemais}
            disabled={isLoading}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!isFormValid || isLoading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {isLoading ? "Aprovando..." : "Confirmar Aprovação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
